import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  formatDateTime as tzFormatDateTime,
  formatDate as tzFormatDate,
  formatRelativeTime as tzFormatRelativeTime,
  getUserTimezone,
} from '../utils/timezoneUtils';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Format date with timezone support
 * @deprecated Use formatDateTime from timezoneUtils for better timezone control
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const userTimezone = getUserTimezone();

  return new Intl.DateTimeFormat('en-US', {
    ...defaultOptions,
    ...options,
    timeZone: userTimezone,
  }).format(typeof date === 'string' ? new Date(date) : date);
}

/**
 * Format date with timezone awareness (recommended)
 */
export function formatDateWithTimezone(
  date: string | Date | null | undefined,
  locale: string = 'en-US'
): string {
  return tzFormatDate(date, locale);
}

/**
 * Format date and time with timezone awareness
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string {
  return tzFormatDateTime(date, options, locale);
}

/**
 * Format relative time with timezone support
 * @deprecated Use formatRelativeTime from timezoneUtils for better timezone control
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDate(targetDate);
}

/**
 * Format relative time with timezone awareness (recommended)
 */
export function formatRelativeTimeWithTimezone(
  date: string | Date | null | undefined,
  locale: string = 'en'
): string {
  return tzFormatRelativeTime(date, locale);
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

export function generateGradient(seed: string): string {
  const gradients = [
    'from-blue-400 to-blue-600',
    'from-purple-400 to-purple-600',
    'from-green-400 to-green-600',
    'from-red-400 to-red-600',
    'from-yellow-400 to-yellow-600',
    'from-indigo-400 to-indigo-600',
    'from-pink-400 to-pink-600',
    'from-teal-400 to-teal-600',
  ];

  const index = seed.length % gradients.length;
  return gradients[index];
}